import { SUPABASE_URL } from "@/integrations/supabase/client";

// Module-level dedup for the link-auth0-identity edge function.
//
// useAuthUser() is mounted by several components at once. Without sharing,
// each instance fired its own link-auth0-identity request on login, producing
// the "[Auth0] linking identity" log flood and redundant edge-function calls.
// Here we cache the resolved profile id per Auth0 subject and collapse
// concurrent calls onto a single in-flight promise, so the link runs once.

export type LinkResult =
  | { ok: true; userId: string }
  | { ok: false; status: number; payload: { error?: string } };

type LinkLogContext = {
  iss?: unknown;
  aud?: unknown;
  email?: unknown;
  email_verified?: unknown;
  exp?: unknown;
};

const cache = new Map<string, string>();          // sub -> profile id
const pending = new Map<string, Promise<LinkResult>>();

export function getCachedAuth0UserId(sub: string): string | null {
  return cache.get(sub) ?? null;
}

export function clearAuth0IdentityLinkCache() {
  cache.clear();
  pending.clear();
}

export async function linkAuth0Identity(
  sub: string,
  token: string,
  logContext?: LinkLogContext,
): Promise<LinkResult> {
  const cached = cache.get(sub);
  if (cached) return { ok: true, userId: cached };

  const inflight = pending.get(sub);
  if (inflight) return inflight;

  const promise = (async (): Promise<LinkResult> => {
    console.info('[Auth0] linking identity', { sub, ...logContext });

    const response = await fetch(`${SUPABASE_URL}/functions/v1/link-auth0-identity`, {
      method: 'POST',
      headers: {
        authorization: `Bearer ${token}`,
        'content-type': 'application/json',
      },
    });

    console.info('[Auth0] link-auth0-identity response', {
      status: response.status,
      ok: response.ok,
    });

    if (!response.ok) {
      const payload = await response.json().catch(() => ({}));
      // Do not cache failures: a user who verifies their email must be able
      // to retry without a stale negative result blocking them.
      return { ok: false, status: response.status, payload };
    }

    const payload = (await response.json()) as { userId?: string };
    if (!payload.userId) {
      return { ok: false, status: response.status, payload: { error: 'Auth0 identity link did not return a profile id' } };
    }

    cache.set(sub, payload.userId);
    return { ok: true, userId: payload.userId };
  })();

  pending.set(sub, promise);
  try {
    return await promise;
  } finally {
    pending.delete(sub);
  }
}
